import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Calculator, Eye, HelpCircle, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import TableSecondary from "../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import ActiveInactiveToggle from "../../../../components/ACS-Dashboard/ACS-Blocks/ActiveInactiveToggle";
import {
  GLOBAL_KPI_DIMENSIONS,
  KPI_CATEGORIES,
  ROLLING_WINDOW_PRESETS,
} from "./kpiCatalogSeed";
import {
  dimensionLabel,
  formatVariantTenantScope,
  getCarrierSelectionGrainInfo,
  isValidKpiCode,
  measureLabel,
  pickInitialRollingWindow,
  suggestVariantDefaults,
  usesLaneRankingGrain,
  isLaneScopedBaseKpi,
  canCalculateCarrierKpisForBase,
} from "./kpiCatalogUtils";
import { buildKpiCalculationTooltip } from "./kpiCalculationHelp";
import {
  collectCarrierKpiNamesForBase,
  fetchCarrierKpisForBaseKpi,
  fetchCarrierKpisForCatalog,
  formatCarrierKpiLaneKey,
  notifyCarrierKpiDataChanged,
  resolveEffectiveAssignedOfficeCodes,
  syncCarrierSelectionVariants,
  syncLaneBaseCarrierKpi,
} from "./api/carrierKpiApi";

const labelClass =
  "mb-1 block text-[11px] font-bold uppercase tracking-wide text-gray-500";

const TD_DATA =
  "border-b border-gray-200 bg-white px-2 text-left text-gray-900 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text sm:px-3";
const TD_MUTED = `${TD_DATA} text-gray-800 dark:text-acs-dark-text`;

const HEADER_TH =
  "whitespace-nowrap border-b border-gray-300 bg-gray-100 px-2 py-2 text-left text-[11px] font-semibold text-gray-700 dark:border-acs-dark-border dark:bg-[#1a1c23] dark:text-acs-dark-text sm:px-3 sm:text-xs md:text-sm transition-none";

const BODY_ROW =
  "border-b border-gray-200 transition-none hover:bg-gray-100 dark:border-acs-dark-border dark:hover:bg-acs-dark-hover cursor-default text-gray-800 dark:text-acs-dark-text [&>td]:py-1.5 [&>td]:text-[11px] sm:[&>td]:text-xs md:[&>td]:text-sm";

function KpiCalculationTooltip({ text, children }) {
  const paragraphs = String(text || "")
    .split(/\n\n+/)
    .map((part) => part.trim())
    .filter(Boolean);

  return (
    <span className="group/kpi-tip relative inline-flex max-w-full min-w-0 items-center gap-1">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none invisible absolute left-0 top-[calc(100%+8px)] z-[200] w-[min(36rem,calc(100vw-2.5rem))] rounded-xl border border-gray-300 bg-white p-4 text-left text-sm leading-6 text-gray-900 shadow-2xl group-hover/kpi-tip:visible group-focus-within/kpi-tip:visible dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text sm:p-5 sm:text-base sm:leading-7"
      >
        <p className="mb-3 text-xs font-bold uppercase tracking-wide text-[#223688] sm:text-sm">
          How this KPI is calculated
        </p>
        {paragraphs.map((paragraph, index) => (
          <p key={index} className={index > 0 ? "mt-3" : ""}>
            {paragraph}
          </p>
        ))}
      </span>
    </span>
  );
}

function ModalShell({ title, children, onClose }) {
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-[#0c1629]">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-gray-500 hover:text-gray-800"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function BaseKpiFormModal({ mode = "create", initialBase, onClose, onSave, saving }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() =>
    isEdit && initialBase
      ? {
          kpiCode: initialBase.kpiCode,
          displayName: initialBase.displayName,
          description: initialBase.description || "",
          category: initialBase.category || "performance",
          enabled: initialBase.enabled !== false,
          supportedDimensions: initialBase.supportedDimensions?.length
            ? [...initialBase.supportedDimensions]
            : ["date", "carrier", "office"],
          measures: initialBase.measures?.length
            ? [...initialBase.measures]
            : ["total_events", "success_events", "rate_pct"],
        }
      : {
          kpiCode: "",
          displayName: "",
          description: "",
          category: "performance",
          enabled: true,
          supportedDimensions: ["date", "carrier", "office"],
          measures: ["total_events", "success_events", "rate_pct"],
        }
  );
  const [errors, setErrors] = useState({});

  const toggleDimension = (dim) => {
    setForm((prev) => ({
      ...prev,
      supportedDimensions: prev.supportedDimensions.includes(dim)
        ? prev.supportedDimensions.filter((d) => d !== dim)
        : [...prev.supportedDimensions, dim],
    }));
  };

  const validate = () => {
    const next = {};
    const code = form.kpiCode.trim();
    if (!code) next.kpiCode = "KPI code is required.";
    else if (!isValidKpiCode(code)) next.kpiCode = "Use lowercase snake_case (e.g. on_time_pickup).";
    if (!form.displayName.trim()) next.displayName = "Display name is required.";
    if (!form.supportedDimensions.length) next.dimensions = "Select at least one dimension.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const payload = {
        kpi_code: form.kpiCode.trim(),
        display_name: form.displayName.trim(),
        description: form.description.trim(),
        category: form.category,
        enabled: form.enabled,
        supported_dimensions: form.supportedDimensions,
        measures: form.measures,
      };
      await onSave(payload, isEdit ? form.kpiCode.trim() : undefined);
      notify.success(isEdit ? "Base KPI updated." : "Base KPI added to library.");
      onClose();
    } catch {
      /* error surfaced by hook */
    }
  };

  return (
    <ModalShell title={isEdit ? "Edit base KPI" : "Add base KPI"} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className={labelClass}>KPI code</label>
          <input
            className={`w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm ${
              isEdit ? "bg-gray-50 text-gray-600" : ""
            }`}
            placeholder="on_time_pickup"
            value={form.kpiCode}
            readOnly={isEdit}
            onChange={(e) => setForm((f) => ({ ...f, kpiCode: e.target.value }))}
          />
          {errors.kpiCode ? <p className="mt-1 text-xs text-red-600">{errors.kpiCode}</p> : null}
        </div>
        <div>
          <label className={labelClass}>Display name</label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          />
          {errors.displayName ? (
            <p className="mt-1 text-xs text-red-600">{errors.displayName}</p>
          ) : null}
        </div>
        <div>
          <label className={labelClass}>Category</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.category}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          >
            {KPI_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Description</label>
          <textarea
            rows={2}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
        <div>
          <label className={labelClass}>Stored dimensions (grain)</label>
          <div className="flex flex-wrap gap-2">
            {GLOBAL_KPI_DIMENSIONS.map((dim) => (
              <button
                key={dim}
                type="button"
                onClick={() => toggleDimension(dim)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  form.supportedDimensions.includes(dim)
                    ? "bg-[#223688] text-white"
                    : "border border-gray-300 bg-white text-gray-700"
                }`}
              >
                {dimensionLabel(dim)}
              </button>
            ))}
          </div>
          {errors.dimensions ? (
            <p className="mt-1 text-xs text-red-600">{errors.dimensions}</p>
          ) : null}
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          Active — variants appear in Resource Selection scoring
        </label>
        {!form.enabled ? (
          <p className="text-xs text-gray-500">
            Inactive KPIs stay in the library but their variants are hidden from configuration
            until reactivated.
          </p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" text="Cancel" onClick={onClose} />
          <Button
            text={saving ? "Saving…" : isEdit ? "Save changes" : "Save KPI"}
            disabled={saving}
            onClick={handleSubmit}
          />
        </div>
      </form>
    </ModalShell>
  );
}

function AddVariantModal({ base, onClose, onSave, saving }) {
  const initialWindow = pickInitialRollingWindow(base, ROLLING_WINDOW_PRESETS);
  const [rollingPreset, setRollingPreset] = useState(String(initialWindow));
  const [scoringKeyMode, setScoringKeyMode] = useState("auto");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState(() =>
    suggestVariantDefaults(base, initialWindow, "auto")
  );
  const [errors, setErrors] = useState({});
  const grainInfo = getCarrierSelectionGrainInfo(base);
  const laneRankingKpi = usesLaneRankingGrain(base);

  const applyRollingWindow = (days, mode = scoringKeyMode) => {
    const next = suggestVariantDefaults(base, days, mode);
    setForm((prev) => ({ ...next, officeScoped: prev.officeScoped }));
  };

  const handlePresetChange = (value) => {
    setRollingPreset(value);
    if (value === "custom") return;
    applyRollingWindow(Number(value));
  };

  const handleScoringKeyModeChange = (mode) => {
    setScoringKeyMode(mode);
    applyRollingWindow(form.rollingWindowDays, mode);
  };

  const validate = () => {
    const next = {};
    if (!form.variantCode.trim()) next.variantCode = "Variant code is required.";
    if (!form.displayName.trim()) next.displayName = "Display name is required.";
    if (!form.scoringWeightKey.trim()) next.scoringWeightKey = "Scoring weight key is required.";
    if (!form.carrierKpiName.trim()) next.carrierKpiName = "Carrier KPI name is required.";
    if (!Number(form.rollingWindowDays) || Number(form.rollingWindowDays) < 1) {
      next.rollingWindowDays = "Rolling window must be at least 1 day.";
    }
    const duplicate = (base.selectionVariants || []).some(
      (v) => v.variantCode === form.variantCode.trim()
    );
    if (duplicate) next.variantCode = "This variant code already exists for this KPI.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      await onSave(base.kpiCode, {
        variant_code: form.variantCode.trim(),
        display_name: form.displayName.trim(),
        scoring_weight_key: form.scoringWeightKey.trim(),
        carrier_kpi_name: form.carrierKpiName.trim(),
        rolling_window_days: Number(form.rollingWindowDays),
        office_scoped: form.officeScoped,
        bridge_enabled: true,
      });
      notify.success("Selection variant added.");
      onClose();
    } catch {
      /* hook sets error */
    }
  };

  return (
    <ModalShell title={`Add selection variant — ${base.displayName}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelClass}>Rolling window</label>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={rollingPreset}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {ROLLING_WINDOW_PRESETS.map((opt) => (
                <option key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </option>
              ))}
              <option value="custom">Custom (days)</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>
              {rollingPreset === "custom" ? "Custom window (days)" : "Window (days)"}
            </label>
            <input
              type="number"
              min={1}
              readOnly={rollingPreset !== "custom"}
              className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-sm ${
                rollingPreset !== "custom" ? "bg-gray-50 text-gray-700" : ""
              }`}
              value={form.rollingWindowDays}
              onChange={(e) => {
                const days = Number(e.target.value);
                setForm((f) => ({ ...f, rollingWindowDays: e.target.value }));
                if (rollingPreset === "custom" && days >= 1) {
                  applyRollingWindow(days);
                }
              }}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Scoring weight key</label>
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={scoringKeyMode}
            onChange={(e) => handleScoringKeyModeChange(e.target.value)}
          >
            <option value="auto">
              Recommended (90d uses base code &quot;{base.kpiCode}&quot;, others use variant code)
            </option>
            <option value="variant">Same as variant code</option>
            <option value="base">Same as base KPI code ({base.kpiCode})</option>
          </select>
        </div>

        <div>
          <label className={labelClass}>Display name</label>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
          />
          {errors.displayName ? (
            <p className="mt-1 text-xs text-red-600">{errors.displayName}</p>
          ) : null}
        </div>

        <div>
          <label className={labelClass}>Tenant scope (carrier_kpi storage)</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, officeScoped: true }))}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                form.officeScoped
                  ? "bg-[#223688] text-white"
                  : "border border-gray-300 bg-white text-gray-700"
              }`}
            >
              Per shipper office
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, officeScoped: false }))}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                !form.officeScoped
                  ? "bg-[#223688] text-white"
                  : "border border-gray-300 bg-white text-gray-700"
              }`}
            >
              Global (all offices)
            </button>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Which shipper offices get their own stored KPI rows. Separate from ranking grain below.
          </p>
        </div>

        <div>
          <label className={labelClass}>Ranking grain (automatic at resource selection)</label>
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                laneRankingKpi
                  ? "bg-emerald-700 text-white"
                  : "border border-gray-200 bg-gray-100 text-gray-400"
              }`}
            >
              Lane (default)
            </span>
            <span
              className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                !laneRankingKpi
                  ? "bg-[#223688] text-white"
                  : "border border-gray-300 bg-white text-gray-600"
              }`}
            >
              Office-wide only
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">{grainInfo.rankingGrainDetail}</p>
        </div>

        <button
          type="button"
          className="text-xs font-semibold text-[#223688] underline"
          onClick={() => setShowAdvanced((v) => !v)}
        >
          {showAdvanced ? "Hide advanced codes" : "Edit variant & carrier codes (advanced)"}
        </button>

        {showAdvanced ? (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div>
              <label className={labelClass}>Variant code</label>
              <input
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm"
                value={form.variantCode}
                onChange={(e) => setForm((f) => ({ ...f, variantCode: e.target.value }))}
              />
              {errors.variantCode ? (
                <p className="mt-1 text-xs text-red-600">{errors.variantCode}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label className={labelClass}>Scoring weight key (override)</label>
                <input
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm"
                  value={form.scoringWeightKey}
                  onChange={(e) => {
                    setScoringKeyMode("custom");
                    setForm((f) => ({ ...f, scoringWeightKey: e.target.value }));
                  }}
                />
                {errors.scoringWeightKey ? (
                  <p className="mt-1 text-xs text-red-600">{errors.scoringWeightKey}</p>
                ) : null}
              </div>
              <div>
                <label className={labelClass}>Carrier KPI name</label>
                <select
                  className="mb-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs"
                  value={
                    form.carrierKpiName === form.variantCode
                      ? "variant"
                      : form.carrierKpiName === form.scoringWeightKey
                        ? "scoring"
                        : "custom"
                  }
                  onChange={(e) => {
                    if (e.target.value === "variant") {
                      setForm((f) => ({ ...f, carrierKpiName: f.variantCode }));
                    } else if (e.target.value === "scoring") {
                      setForm((f) => ({ ...f, carrierKpiName: f.scoringWeightKey }));
                    }
                  }}
                >
                  <option value="variant">Match variant code</option>
                  <option value="scoring">Match scoring key</option>
                  <option value="custom">Custom (edit below)</option>
                </select>
                <input
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm"
                  value={form.carrierKpiName}
                  onChange={(e) => setForm((f) => ({ ...f, carrierKpiName: e.target.value }))}
                />
                {errors.carrierKpiName ? (
                  <p className="mt-1 text-xs text-red-600">{errors.carrierKpiName}</p>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <p>
              <span className="font-semibold text-gray-700">Variant:</span>{" "}
              <span className="font-mono">{form.variantCode}</span>
            </p>
            <p className="mt-1">
              <span className="font-semibold text-gray-700">Scoring key:</span>{" "}
              <span className="font-mono">{form.scoringWeightKey}</span>
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Pick a rolling window — codes and names fill in automatically. Use advanced only if you
          need a custom scoring key.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" text="Cancel" onClick={onClose} />
          <Button text={saving ? "Saving…" : "Save variant"} disabled={saving} onClick={handleSubmit} />
        </div>
      </form>
    </ModalShell>
  );
}

function KpiVariantsPanel({ base, saving, deletingVariantCode, onAddVariant, onEdit, onDeleteVariant }) {
  const variants = base.selectionVariants || [];

  return (
    <div className="border-t border-gray-200 bg-gray-50 px-3 py-3 dark:border-acs-dark-border dark:bg-acs-dark-hover sm:px-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => onEdit?.(base)}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-[#223688] hover:bg-gray-50 disabled:opacity-50"
        >
          Edit KPI
        </button>
        <button
          type="button"
          onClick={() => onAddVariant(base)}
          className="inline-flex items-center gap-1 rounded-md bg-[#223688] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#1a2d66]"
        >
          <Plus size={14} />
          Add variant
        </button>
      </div>
      {base.description ? (
        <p className="mb-3 text-sm text-gray-600 dark:text-acs-dark-subtext">{base.description}</p>
      ) : null}
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">Selection variants</p>
      {variants.length ? (
        <TableSecondary
          shipmentListStyle
          headers={["Variant", "Scoring key", "Window", "Scope", "Carrier KPI", ""]}
          data={variants}
          columnWidths={["24%", "18%", "10%", "10%", "24%", "52px"]}
          renderRow={(variant) => {
            const isDeleting = deletingVariantCode === variant.variantCode;
            return (
              <>
                <td className={`${TD_DATA} max-w-0`}>
                  <p className="truncate font-medium">{variant.displayName}</p>
                  <p className="truncate font-mono text-[11px] text-gray-500">{variant.variantCode}</p>
                </td>
                <td className={`${TD_MUTED} font-mono text-xs`}>{variant.scoringWeightKey}</td>
                <td className={`${TD_MUTED} tabular-nums`}>
                  {variant.fixedDimensions?.rollingWindowDays
                    ? `${variant.fixedDimensions.rollingWindowDays}d`
                    : "—"}
                </td>
                <td className={TD_MUTED}>
                  <span className="block">
                    {formatVariantTenantScope(Boolean(variant.fixedDimensions?.officeScoped))}
                  </span>
                  <span className="block text-[10px] text-gray-500">
                    {usesLaneRankingGrain(base)
                      ? "Ranking: lane or neutral (1.0)"
                      : "Ranking: office-wide only"}
                  </span>
                </td>
                <td className={`${TD_MUTED} font-mono text-xs`}>{variant.carrierKpiName}</td>
                <td
                  className={`${TD_DATA} text-center`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    title="Delete variant"
                    aria-label={`Delete ${variant.displayName}`}
                    disabled={saving || isDeleting}
                    onClick={() => onDeleteVariant?.(base, variant)}
                    className={`inline-flex items-center justify-center rounded p-1 text-red-600 transition-none hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30 ${
                      isDeleting ? "cursor-wait opacity-50" : ""
                    }`}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </>
            );
          }}
        />
      ) : (
        <p className="text-sm text-gray-500">No selection variants yet.</p>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        Measures: {(base.measures || []).map(measureLabel).join(", ")}
      </p>
    </div>
  );
}

function formatCarrierKpiValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return value ?? "—";
  return num.toFixed(4);
}

function formatCarrierKpiDate(value) {
  if (!value) return "—";
  const str = String(value);
  return str.length >= 10 ? str.slice(0, 10) : str;
}

function formatCarrierKpiDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

const CARRIER_KPI_ALL_OFFICES = "All Offices";
const CARRIER_KPI_ALL_BASES = "All KPIs";

const CARRIER_KPI_SORTABLE_COLUMNS = {
  Carrier: "carrierCode",
  Office: "officeCode",
  Lane: "laneKey",
  "Base KPI": "baseDisplayName",
  "KPI name": "kpiName",
  Value: "kpiValue",
  "Window start": "windowStart",
  "Window end": "windowEnd",
  "Computed at": "computedAt",
};

function compareCarrierKpiRows(a, b, sortBy, ascending) {
  const direction = ascending ? 1 : -1;

  if (sortBy === "kpiValue") {
    const aNum = Number(a[sortBy]);
    const bNum = Number(b[sortBy]);
    const aVal = Number.isFinite(aNum) ? aNum : null;
    const bVal = Number.isFinite(bNum) ? bNum : null;
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    return (aVal - bVal) * direction;
  }

  if (sortBy === "computedAt" || sortBy === "windowStart" || sortBy === "windowEnd") {
    const aTime = a[sortBy] ? new Date(a[sortBy]).getTime() : NaN;
    const bTime = b[sortBy] ? new Date(b[sortBy]).getTime() : NaN;
    const aVal = Number.isFinite(aTime) ? aTime : null;
    const bVal = Number.isFinite(bTime) ? bTime : null;
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    return (aVal - bVal) * direction;
  }

  const aVal = String(a[sortBy] ?? "").toLowerCase();
  const bVal = String(b[sortBy] ?? "").toLowerCase();
  if (aVal < bVal) return -1 * direction;
  if (aVal > bVal) return 1 * direction;
  return 0;
}

function CarrierKpiViewPanel({
  catalog = [],
  baseKpiFilter = CARRIER_KPI_ALL_BASES,
  searchTerm = "",
  officeFilter = CARRIER_KPI_ALL_OFFICES,
  isActive = true,
  onMetaChange,
}) {
  const [rows, setRows] = useState([]);
  const [kpiNames, setKpiNames] = useState([]);
  const [assignedOfficeCodes, setAssignedOfficeCodes] = useState([]);
  const [totalBeforeOfficeFilter, setTotalBeforeOfficeFilter] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("baseDisplayName");
  const [ascending, setAscending] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const loadRef = useRef(null);

  const catalogLoadKey = useMemo(
    () =>
      catalog
        .map(
          (base) =>
            `${base.kpiCode}:${(base.selectionVariants || [])
              .map((v) => v.variantCode)
              .join(",")}`
        )
        .join("|"),
    [catalog]
  );

  const load = useCallback(async () => {
    if (!catalogLoadKey) {
      setRows([]);
      setKpiNames([]);
      setAssignedOfficeCodes(resolveEffectiveAssignedOfficeCodes());
      setTotalBeforeOfficeFilter(0);
      setLoading(false);
      return;
    }
    const offices = resolveEffectiveAssignedOfficeCodes();
    setLoading(true);
    setError("");
    try {
      const filteredBase =
        baseKpiFilter !== CARRIER_KPI_ALL_BASES
          ? catalog.find((base) => base.kpiCode === baseKpiFilter)
          : null;
      const result = filteredBase
        ? await fetchCarrierKpisForBaseKpi(filteredBase, { allowedOfficeCodes: offices })
        : await fetchCarrierKpisForCatalog(catalog, { allowedOfficeCodes: offices });
      setRows(result.rows);
      setKpiNames(result.kpiNames);
      setAssignedOfficeCodes(result.officesUsed?.length ? result.officesUsed : offices);
      setTotalBeforeOfficeFilter(result.totalBeforeOfficeFilter ?? 0);
    } catch (err) {
      setRows([]);
      setKpiNames([]);
      setAssignedOfficeCodes(offices);
      setTotalBeforeOfficeFilter(0);
      setError(err?.message || "Could not load carrier KPIs.");
    } finally {
      setLoading(false);
    }
  }, [catalog, catalogLoadKey, baseKpiFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onDataChanged = () => loadRef.current?.();
    window.addEventListener("carrier-kpi-data-changed", onDataChanged);
    return () => window.removeEventListener("carrier-kpi-data-changed", onDataChanged);
  }, []);

  loadRef.current = load;

  const officeOptions = useMemo(() => {
    const codes = [...new Set(rows.map((row) => row.officeCode || "Global"))].sort();
    return [CARRIER_KPI_ALL_OFFICES, ...codes];
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (baseKpiFilter !== CARRIER_KPI_ALL_BASES) {
      list = list.filter((row) => row.baseKpiCode === baseKpiFilter);
    }
    if (officeFilter !== CARRIER_KPI_ALL_OFFICES) {
      list = list.filter((row) => (row.officeCode || "Global") === officeFilter);
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((row) =>
        [
          row.carrierCode,
          row.officeCode,
          row.laneKey,
          formatCarrierKpiLaneKey(row.laneKey),
          row.baseDisplayName,
          row.baseKpiCode,
          row.kpiName,
          row.kpiValue,
        ]
          .filter((value) => value != null && value !== "")
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return list;
  }, [rows, baseKpiFilter, officeFilter, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    return [...filteredRows].sort((a, b) => compareCarrierKpiRows(a, b, sortBy, ascending));
  }, [filteredRows, sortBy, ascending]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / itemsPerPage));

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [baseKpiFilter, officeFilter, searchTerm, sortBy, ascending]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSort = useCallback((headerLabel) => {
    const key = CARRIER_KPI_SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    if (sortBy === key) {
      setAscending((prev) => !prev);
    } else {
      setSortBy(key);
      setAscending(true);
    }
  }, [sortBy]);

  useEffect(() => {
    if (!isActive || !onMetaChange) return;
    onMetaChange({
      loading,
      filteredCount: filteredRows.length,
      officeOptions,
      refresh: () => loadRef.current?.(),
    });
  }, [
    isActive,
    onMetaChange,
    loading,
    filteredRows.length,
    officeOptions,
  ]);

  const activeBaseLabel =
    baseKpiFilter === CARRIER_KPI_ALL_BASES
      ? null
      : catalog.find((base) => base.kpiCode === baseKpiFilter)?.displayName;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface">
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-acs-dark-border">
        <div>
          <h3 className="text-lg font-semibold text-[#0c1629] dark:text-acs-dark-text">
            {activeBaseLabel ? `Carrier KPIs — ${activeBaseLabel}` : "Carrier KPIs"}
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-acs-dark-subtext">
            {kpiNames.length ? `${kpiNames.length} KPI name variant(s)` : "No KPI variants in catalog"}
            {assignedOfficeCodes.length
              ? ` · Offices: ${assignedOfficeCodes.join(", ")}`
              : " · No assigned offices"}
          </p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4">
        {error ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {error}
          </div>
        ) : loading ? (
          <Loader message="Loading carrier KPIs…" />
        ) : filteredRows.length ? (
          <>
            <div className="min-h-0 flex-1 overflow-auto">
              <TableSecondary
                shipmentListStyle
                headers={[
                  "Carrier",
                  "Office",
                  "Lane",
                  "Base KPI",
                  "KPI name",
                  "Value",
                  "Window start",
                  "Window end",
                  "Computed at",
                ]}
                columnWidths={["10%", "9%", "18%", "12%", "12%", "9%", "10%", "10%", "10%"]}
                data={paginatedRows}
                onSort={handleSort}
                sortBy={sortBy}
                ascending={ascending}
                sortKeyByHeader={(label) => CARRIER_KPI_SORTABLE_COLUMNS[label] || null}
                renderRow={(row) => (
                  <>
                    <td className={`${TD_DATA} font-medium`}>{row.carrierCode || "—"}</td>
                    <td className={TD_MUTED}>{row.officeCode || "Global"}</td>
                    <td
                      className={`${TD_MUTED} max-w-[14rem] truncate text-xs`}
                      title={row.laneKey ? row.laneKey : "Office-wide aggregate (no lane corridor)"}
                    >
                      {formatCarrierKpiLaneKey(row.laneKey)}
                    </td>
                    <td className={`${TD_MUTED} truncate`} title={row.baseDisplayName}>
                      {row.baseDisplayName || "—"}
                    </td>
                    <td className={`${TD_MUTED} font-mono text-xs`}>{row.kpiName}</td>
                    <td className={`${TD_MUTED} tabular-nums`}>{formatCarrierKpiValue(row.kpiValue)}</td>
                    <td className={TD_MUTED}>{formatCarrierKpiDate(row.windowStart)}</td>
                    <td className={TD_MUTED}>{formatCarrierKpiDate(row.windowEnd)}</td>
                    <td className={`${TD_MUTED} text-xs`}>{formatCarrierKpiDateTime(row.computedAt)}</td>
                  </>
                )}
              />
            </div>
            <GlobalPagination
              label="Rows per page"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          </>
        ) : (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-acs-dark-subtext">
            {!assignedOfficeCodes.length
              ? "No assigned offices found for your account."
              : totalBeforeOfficeFilter > 0
                ? `No carrier KPI rows match your assigned offices (${assignedOfficeCodes.join(", ")}). ${totalBeforeOfficeFilter} row(s) exist for other offices or KPI names queried: ${kpiNames.join(", ")}.`
                : "No carrier KPI rows found. Run Calculate on a KPI in the library to backfill values."}
          </p>
        )}
      </div>
    </div>
  );
}

export {
  CarrierKpiViewPanel,
  CARRIER_KPI_ALL_OFFICES,
  CARRIER_KPI_ALL_BASES,
};

const KpiLibraryAdmin = ({
  catalog,
  loading,
  saving,
  error,
  reload,
  saveBaseKpi,
  updateBaseKpi,
  saveSelectionVariant,
  deleteSelectionVariant,
  onOpenCarrierKpiTab,
}) => {

  const [expandedCode, setExpandedCode] = useState(null);
  const [showCreateBase, setShowCreateBase] = useState(false);
  const [editBase, setEditBase] = useState(null);
  const [variantForBase, setVariantForBase] = useState(null);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [togglingKpiCode, setTogglingKpiCode] = useState(null);
  const [deletingVariantCode, setDeletingVariantCode] = useState(null);
  const [syncingKpiCode, setSyncingKpiCode] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        const trigger = e.target.closest("[data-kpi-action-trigger]");
        if (!trigger) setOpenDropdownRow(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleActionClick = (rowKey, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    setOpenDropdownRow((prev) => (prev === rowKey ? null : rowKey));
  };

  const handleSaveBase = async (payload, kpiCodeForEdit) => {
    if (kpiCodeForEdit) {
      await updateBaseKpi(kpiCodeForEdit, payload);
    } else {
      await saveBaseKpi(payload);
    }
  };

  const handleToggleActive = async (base, enabled) => {
    setTogglingKpiCode(base.kpiCode);
    try {
      await updateBaseKpi(base.kpiCode, { enabled });
      notify.success(enabled ? "KPI set to active." : "KPI set to inactive.");
    } catch {
      /* hook sets error */
    } finally {
      setTogglingKpiCode(null);
    }
  };

  const handleDeleteVariant = async (_base, variant) => {
    const code = variant?.variantCode;
    if (!code || deletingVariantCode === code) return;
    const label = variant.displayName || code;
    if (
      !window.confirm(
        `Delete variant "${label}" (${code})? This cannot be undone.`
      )
    ) {
      return;
    }
    setDeletingVariantCode(code);
    try {
      await deleteSelectionVariant(code);
      notify.success("Selection variant deleted.");
    } catch (err) {
      notify.error({
        title: "Delete failed",
        message: err?.message || "Unable to delete this variant.",
      });
    } finally {
      setDeletingVariantCode(null);
    }
  };

  const handleSyncCarrierKpis = async (base) => {
    const laneBase = isLaneScopedBaseKpi(base);
    const variants = (base.selectionVariants || []).filter((v) => v?.variantCode);

    const officeCodes = resolveEffectiveAssignedOfficeCodes();
    if (!officeCodes.length) {
      notify.error({
        title: "No offices",
        message: "Your account has no assigned offices. Carrier KPI calculation requires at least one office.",
      });
      return;
    }

    setSyncingKpiCode(base.kpiCode);
    try {
      if (laneBase) {
        await syncLaneBaseCarrierKpi({
          kpiCode: base.kpiCode,
          officeCodes,
        });
        for (const variant of variants) {
          await syncCarrierSelectionVariants({
            variantCode: variant.variantCode,
            officeCodes,
          });
        }
      } else if (variants.length) {
        for (const variant of variants) {
          await syncCarrierSelectionVariants({
            variantCode: variant.variantCode,
            officeCodes,
          });
        }
      }
      const scopeMsg = laneBase
        ? `lane base${variants.length ? ` + ${variants.length} variant(s)` : ""} for "${base.displayName}"`
        : variants.length
          ? `${variants.length} variant(s) on "${base.displayName}"`
          : `"${base.displayName}" (no selection variants — add variants to write carrier_kpi rows)`;
      notify.success({
        title: `Calculation complete (${base.displayName})`,
        message: `Recomputed ${scopeMsg}.`,
      });
      notifyCarrierKpiDataChanged();
    } catch (err) {
      notify.error({
        title: "Calculation failed",
        message: err?.message || "Could not calculate carrier-level KPIs.",
      });
    } finally {
      setSyncingKpiCode(null);
    }
  };

  const handleViewCarrierKpis = (base) => {
    if (typeof onOpenCarrierKpiTab === "function") {
      onOpenCarrierKpiTab(base);
      return;
    }
    notify.error({
      title: "Unable to open tab",
      message: "Carrier KPI view is not available in this context.",
    });
  };

  const sortedCatalog = useMemo(
    () =>
      [...catalog].sort(
        (a, b) =>
          Number(b.enabled !== false) - Number(a.enabled !== false) ||
          a.displayName.localeCompare(b.displayName)
      ),
    [catalog]
  );

  const toggleExpanded = useCallback((kpiCode) => {
    setExpandedCode((prev) => (prev === kpiCode ? null : kpiCode));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-surface">
        <Loader message="Loading KPI library…" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">

      {error ? (
        <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {error}{" "}
          <button type="button" className="font-semibold underline" onClick={reload}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface transition-none">
          <table className="min-w-full table-fixed border-collapse bg-white font-roboto dark:bg-acs-dark-surface transition-none">
            <colgroup>
              <col style={{ width: "52px" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "200px" }} />
            </colgroup>
            <thead className="sticky top-0 z-[10] bg-gray-100 shadow-md transition-none dark:bg-acs-dark-surface">
              <tr>
                <th className={HEADER_TH}>
                  <span className="sr-only">Actions</span>
                  <MoreHorizontal size={16} className="text-gray-600 dark:text-gray-300" aria-hidden />
                </th>
                <th className={HEADER_TH}>KPI name</th>
                <th className={HEADER_TH}>Code</th>
                <th className={HEADER_TH}>Status</th>
                <th className={HEADER_TH}>Category</th>
                <th className={HEADER_TH}>Dimensions</th>
                <th className={HEADER_TH}>Variants</th>
                <th className={HEADER_TH}>Measures</th>
                <th className={HEADER_TH}>Carrier KPIs</th>
              </tr>
            </thead>
            <tbody>
              {sortedCatalog.length ? (
                sortedCatalog.flatMap((base) => {
                  const rowKey = base.kpiCode;
                  const isOpen = openDropdownRow === rowKey;
                  const isExpanded = expandedCode === base.kpiCode;
                  const variantCount = (base.selectionVariants || []).length;
                  const dimensionSummary = (base.supportedDimensions || [])
                    .map(dimensionLabel)
                    .join(", ");
                  const measuresSummary = (base.measures || []).map(measureLabel).join(", ");
                  const calculationTooltip = buildKpiCalculationTooltip(base);
                  const isSyncingCarrierKpis = syncingKpiCode === base.kpiCode;
                  const laneScopedBase = isLaneScopedBaseKpi(base);
                  const canSyncCarrierKpis =
                    canCalculateCarrierKpisForBase(base) && !saving && !isSyncingCarrierKpis;
                  const calculateDisabledReason = laneScopedBase
                    ? "Calculate lane-level carrier_kpi rows (base + any variants) for assigned offices"
                    : variantCount === 0
                      ? "Calculate carrier KPIs (add selection variants to write rows for office KPIs)"
                      : "Calculate carrier-level KPIs from selection variants";

                  const actions = [
                    {
                      icon: "Eye",
                      label: isExpanded ? "Hide details" : "View details",
                      color: "blue",
                      action: () => {
                        setOpenDropdownRow(null);
                        toggleExpanded(base.kpiCode);
                      },
                    },
                    {
                      icon: "Edit",
                      label: "Edit KPI",
                      color: "green",
                      action: () => {
                        setOpenDropdownRow(null);
                        setEditBase(base);
                      },
                    },
                    {
                      icon: "FilePlus",
                      label: "Add variant",
                      color: "blue",
                      action: () => {
                        setOpenDropdownRow(null);
                        setVariantForBase(base);
                      },
                    },
                    {
                      icon: "Calculator",
                      label: isSyncingCarrierKpis
                        ? "Calculating carrier KPIs…"
                        : "Calculate carrier KPIs",
                      color: "orange",
                      disabled: !canSyncCarrierKpis,
                      tooltip: calculateDisabledReason,
                      action: () => {
                        setOpenDropdownRow(null);
                        handleSyncCarrierKpis(base);
                      },
                    },
                  ];

                  const rows = [
                    <tr
                      key={rowKey}
                      className={`${BODY_ROW} ${isExpanded ? "bg-gray-50 dark:bg-acs-dark-hover" : ""}`}
                    >
                      <td className={`${TD_DATA} align-middle`} onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          data-kpi-action-trigger
                          className="inline-flex items-center justify-start p-1 text-gray-500 transition-none hover:text-gray-800 dark:text-gray-400 dark:hover:text-acs-dark-text"
                          onClick={(e) => handleActionClick(rowKey, e)}
                          aria-label="Row actions"
                        >
                          <MoreHorizontal size={18} className="shrink-0" />
                        </button>
                        {isOpen &&
                          ReactDOM.createPortal(
                            <div
                              ref={dropdownRef}
                              style={{
                                position: "absolute",
                                top: dropdownPos.top,
                                left: dropdownPos.left,
                                zIndex: 9999,
                              }}
                            >
                              <ActionMenu
                                rowId={rowKey}
                                isOpen
                                onClose={() => setOpenDropdownRow(null)}
                                moduleName="KPI library entry"
                                actions={actions}
                              />
                            </div>,
                            document.body
                          )}
                      </td>
                      <td className={`${TD_DATA} max-w-0 overflow-visible font-medium`}>
                        <KpiCalculationTooltip text={calculationTooltip}>
                          <button
                            type="button"
                            className="inline-flex max-w-full min-w-0 items-center gap-1 truncate text-left hover:text-[#223688] hover:underline"
                            onClick={() => toggleExpanded(base.kpiCode)}
                            aria-describedby={`kpi-calc-tip-${rowKey}`}
                          >
                            <span className="truncate">{base.displayName}</span>
                            <HelpCircle
                              size={16}
                              className="shrink-0 text-gray-500 group-hover/kpi-tip:text-[#223688] dark:text-gray-400"
                              aria-hidden
                            />
                          </button>
                          <span id={`kpi-calc-tip-${rowKey}`} className="sr-only">
                            {calculationTooltip}
                          </span>
                        </KpiCalculationTooltip>
                      </td>
                      <td className={`${TD_MUTED} max-w-0 truncate font-mono text-xs`}>
                        {base.kpiCode}
                      </td>
                      <td className={TD_MUTED} onClick={(e) => e.stopPropagation()}>
                        <ActiveInactiveToggle
                          checked={base.enabled !== false}
                          disabled={saving || togglingKpiCode === base.kpiCode}
                          labelId={`kpi-status-${rowKey}`}
                          onChange={(next) => handleToggleActive(base, next)}
                        />
                      </td>
                      <td className={`${TD_MUTED} capitalize truncate`}>{base.category || "—"}</td>
                      <td className={`${TD_MUTED} max-w-0 truncate`} title={dimensionSummary}>
                        {dimensionSummary || "—"}
                      </td>
                      <td className={`${TD_MUTED} tabular-nums`}>{variantCount}</td>
                      <td className={`${TD_MUTED} max-w-0 truncate`} title={measuresSummary}>
                        {measuresSummary || "—"}
                      </td>
                      <td
                        className={`${TD_MUTED} min-w-[11.5rem]`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-nowrap items-center gap-1">
                          <button
                            type="button"
                            title={calculateDisabledReason}
                            aria-label={
                              isSyncingCarrierKpis
                                ? "Calculating carrier KPIs"
                                : "Calculate carrier KPIs"
                            }
                            disabled={!canSyncCarrierKpis}
                            onClick={() => handleSyncCarrierKpis(base)}
                            className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-1 text-[11px] font-semibold whitespace-nowrap transition-none ${
                              canSyncCarrierKpis
                                ? "border-[#223688] bg-white text-[#223688] hover:bg-[#223688] hover:text-white dark:border-acs-dark-border dark:bg-acs-dark-surface dark:hover:bg-[#223688]"
                                : "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-acs-dark-border dark:bg-acs-dark-hover dark:text-gray-500"
                            } ${isSyncingCarrierKpis ? "cursor-wait opacity-70" : ""}`}
                          >
                            <Calculator size={14} className="shrink-0" />
                            {isSyncingCarrierKpis ? "…" : "Calculate"}
                          </button>
                          <button
                            type="button"
                            title="View carrier_kpi rows for all carriers for this KPI"
                            aria-label="View carrier KPIs"
                            onClick={() => handleViewCarrierKpis(base)}
                            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 bg-white px-1.5 py-1 text-[11px] font-semibold whitespace-nowrap text-gray-700 transition-none hover:bg-gray-50 dark:border-acs-dark-border dark:bg-acs-dark-surface dark:text-acs-dark-text dark:hover:bg-acs-dark-hover"
                          >
                            <Eye size={14} className="shrink-0" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>,
                  ];

                  if (isExpanded) {
                    rows.push(
                      <tr key={`${rowKey}-detail`} className="border-b border-gray-200 dark:border-acs-dark-border">
                        <td colSpan={9} className="p-0">
                          <KpiVariantsPanel
                            base={base}
                            saving={saving}
                            deletingVariantCode={deletingVariantCode}
                            onAddVariant={setVariantForBase}
                            onEdit={setEditBase}
                            onDeleteVariant={handleDeleteVariant}
                          />
                        </td>
                      </tr>
                    );
                  }

                  return rows;
                })
              ) : (
                <tr>
                  <td
                    colSpan={9}
                    className="px-fluid-3 py-fluid-4 text-center text-fluid-sm text-gray-500 dark:text-acs-dark-subtext"
                  >
                    No KPIs match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
      </div>

      {showCreateBase ? (
        <BaseKpiFormModal
          mode="create"
          saving={saving}
          onClose={() => setShowCreateBase(false)}
          onSave={handleSaveBase}
        />
      ) : null}

      {editBase ? (
        <BaseKpiFormModal
          mode="edit"
          initialBase={editBase}
          saving={saving}
          onClose={() => setEditBase(null)}
          onSave={handleSaveBase}
        />
      ) : null}

      {variantForBase ? (
        <AddVariantModal
          base={variantForBase}
          saving={saving}
          onClose={() => setVariantForBase(null)}
          onSave={saveSelectionVariant}
        />
      ) : null}
    </div>
  );
};

export default KpiLibraryAdmin;
